import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom'
import { LoginSocialGoogle, LoginSocialFacebook, LoginSocialApple } from 'reactjs-social-login';
import identityFetcher from 'api/identityFetcher';
import FormSignInViaCreds from 'components/FormSignInViaCreds';
import PreloaderSpin from 'components/PreloaderSpin';
import IconSocial from 'components/IconSocial';
import { convertKeysToCamelCase, convertKeysToSnakeCase, genUnsignedJWT, decodeBase64 } from 'utils/helpers';
import { SSO_PROVIDERS, RESPONSE_PROVIDERS } from 'constants/auth';
import config from 'config';
import axios from 'axios';

const STORAGE_KEY = 'app.auth.session';
const DEVICE_ID = 'f623b75d-ab63-4a53-a592-7e7976526a0d';


const ERROR_MESSAGE_BY_DEFAULT = 'Something went wrong';
const ERROR_MESSAGE_INVALID_DATA = 'Invalid data format received';
const ERROR_MESSAGE_INTERNAL_ERROR = 'Internal server error';

const proxyFetcher = axios.create({
  baseURL: 'https://account.dev.yomobile.com/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

const App = () => {
  const storedAuthSession = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  const [inProcessing, setInProcessing] = useState(false);
  const [authSession, setAuthSession] = useState(storedAuthSession);
  const [isAuthorizedUser, setIsAuthorizedUser] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (!authSession || !authSession?.user?.id) {
      localStorage.removeItem(STORAGE_KEY);
      setIsAuthorizedUser(false);
      return;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(authSession));
    setIsAuthorizedUser(true);
  }, [authSession]);


  useEffect(() => {
    if (!inProcessing || !error) return;
    setError(null);
  }, [inProcessing, error]);

  const loginViaSocialProvider = async (provider, { code, tokenType = 'Bearer', accessToken, displayName = 'Test User' }) => {
    const params = {
      deviceId: DEVICE_ID,
      provider,
      code,
      displayName,
    };

    try {
      const session = await identityFetcher.post('/login/social/?platform=yo_web', params, {
        headers: {
          accept: 'application/json',
          ...(accessToken && { Authorization: `${tokenType} ${accessToken}` }),
          'Content-Type': 'application/json',
          'X-PLATFORM': 'yo_web',
        }
      });
      console.log(`🚀 ~ social login (${provider}) ~ session:`, session);
      setAuthSession({ ...session, provider });
    } catch (error) {
      setError({
        provider: RESPONSE_PROVIDERS.BACKEND,
        message: error?.message || ERROR_MESSAGE_BY_DEFAULT
      });
    } finally {
      setInProcessing(false);
    }
  };

  const handleLoginViaCreds = async (params, provider = 'credentials') => {
    setInProcessing(true);
    try {
      const session = await identityFetcher.post('/login/?platform=yo', { ...params, deviceId: DEVICE_ID });
      console.log(`🚀 ~ login (${provider}) ~ session:`, session);
      setAuthSession({ ...session, provider });
    } catch (error) {
      setError({
        provider: RESPONSE_PROVIDERS.BACKEND,
        message: error?.message || ERROR_MESSAGE_BY_DEFAULT
      });
    } finally {
      setInProcessing(false);
    }
  };

  const handleLoginViaSSO = async (provider, data) => {
    if (!provider || !data) return;

    /*
     * Authorization via Google
     */
    if (provider === SSO_PROVIDERS.GOOGLE) {
      if (!data?.code) {
        setError({ provider, message: ERROR_MESSAGE_INVALID_DATA });
        setInProcessing(false);
        return;
      }

      if (location.pathname !== '/use-proxy') return await loginViaSocialProvider(provider, { code: data.code });

      const { id_token: idToken, access_token: accessToken, token_type: tokenType } = await proxyFetcher.post('/exchange-oauth-code-for-token', {
        provider,
        code: data.code,
      }).catch(() => ({})).finally(() => setInProcessing(false));

      if (!idToken || !accessToken) {
        setError({ provider: RESPONSE_PROVIDERS.PROXY, message: ERROR_MESSAGE_INTERNAL_ERROR });
        setInProcessing(false);
        return;
      }

      const userProfile = decodeBase64(idToken.split('.')[1]);

      await loginViaSocialProvider(provider, {
        code: idToken,
        tokenType,
        accessToken,
        displayName: userProfile.name,
      });
    }

    /*
     * Authorization via Facebook
     */
    if (provider === SSO_PROVIDERS.FACEBOOK) {
      const { accessToken, expiresIn, signedRequest, grantedScopes, ...user } = data;
      const idToken = genUnsignedJWT(convertKeysToSnakeCase(user));

      if (!accessToken || !idToken || !user?.email) {
        setError({
          provider,
          message: ERROR_MESSAGE_INVALID_DATA
        });
        setInProcessing(false);
        return;
      }

      const params = {
        deviceId: DEVICE_ID,
        provider,
        code: idToken,
        displayName: user?.name || 'Test User',
      }

      try {
        const session = await identityFetcher.post('/login/social/?platform=yo_web', params, {
          headers: {
            accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'X-PLATFORM': 'yo_web',
          }
        });
        console.log(`🚀 ~ social login (${provider}) ~ session:`, session);
        setAuthSession({ ...session, provider });
      } catch (error) {
        setError({
          provider: RESPONSE_PROVIDERS.BACKEND,
          message: error?.message || ERROR_MESSAGE_BY_DEFAULT
        });
      } finally {
        setInProcessing(false);
      }
    }
  };

  const handleLogout = () => {
    setAuthSession({});
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-800 p-12 lg:p-24">
      <div className={`bg-white p-0 rounded-2xl shadow-lg ${isAuthorizedUser ? 'max-w-3xl w-full m-8' : 'w-md'} text-center`}>
        <div className="p-8">
          {isAuthorizedUser ? (
            <div className="relative">
              <h2 className="uppercase text-xl text-left font-semibold mb-6">Authorized user</h2>
              {authSession.provider && (
                <div className="max-w-48 absolute top-0 right-0">via <span className="bg-black text-white px-2 py-1 text-xs uppercase rounded">{authSession.provider}</span></div>
              )}
              <div className="flex flex-col text-left gap-2 relative">
                <h3 className="font-semibold">Response from backend:</h3>
                <div className="text-sm text-gray-800 max-h-96 overflow-y-auto">
                  <pre className="whitespace-pre-wrap break-words p-4 bg-gray-100 text-xs">{JSON.stringify(authSession, null, 2)}</pre>
                </div>
                <button onClick={handleLogout} type="button" className="w-full mt-4 py-4 px-12 rounded-full max-w-64 m-auto bg-indigo-600 hover:not-focus:bg-indigo-700 text-lg text-white font-bold p-2 disabled:opcaity-50 hover:cursor-pointer">
                  Logout
                </button>
              </div>
            </div>
          ) : (
            <>
              <h2 className="uppercase text-xl font-semibold mb-6">Sign In</h2>
              <div className="relative">
                <div className="flex justify-center gap-4 mb-2">
                  <LoginSocialGoogle
                    client_id={config.googleClientId}
                    redirect_uri={`${config.siteUrl}/auth/callback/google`}
                    // isOnlyGetCode={true}
                    access_type="offline"
                    scope="openid profile email"
                    response_type="id_token token"
                    onLoginStart={() => setInProcessing(true)}
                    onResolve={(response) => handleLoginViaSSO(SSO_PROVIDERS.GOOGLE, convertKeysToCamelCase(response?.data))}
                    onReject={(err) => {
                      setInProcessing(false);
                      setError({
                        provider: err?.provider || SSO_PROVIDERS.GOOGLE,
                        message: err?.data?.message || ERROR_MESSAGE_BY_DEFAULT
                      });
                      console.error("Google login failed", err, err?.data?.message);
                    }}
                  >
                    <IconSocial provider={SSO_PROVIDERS.GOOGLE} />
                  </LoginSocialGoogle>

                  <LoginSocialFacebook
                    appId={config.facebookAppId}
                    scope="openid email"
                    // isOnlyGetToken={true}
                    onLoginStart={() => setInProcessing(true)}
                    onResolve={({ data }) => handleLoginViaSSO(SSO_PROVIDERS.FACEBOOK, data)}
                    onReject={(err) => {
                      setInProcessing(false);
                      setError({
                        provider: err?.provider || SSO_PROVIDERS.FACEBOOK,
                        message: err?.data?.message || ERROR_MESSAGE_BY_DEFAULT
                      })
                      console.error("Facebook login failed", err);
                    }}
                  >
                    <IconSocial provider={SSO_PROVIDERS.FACEBOOK} isLocked />
                  </LoginSocialFacebook>

                  <LoginSocialApple
                    client_id={config.appleClientId}
                    redirect_uri={`${config.siteUrl}/auth/callback/apple`}
                    scope="name email"
                    response_type="code"
                    onResolve={({ data }) => handleLoginViaSSO(SSO_PROVIDERS.APPLE, data)}
                    onReject={() => {}}
                  >
                    <IconSocial provider={SSO_PROVIDERS.APPLE} isLocked />
                  </LoginSocialApple>
                </div>

                <a href={location.pathname === '/use-proxy' ? '/' : '/use-proxy'} className="flex flex-row gap-2 mb-4 font2-semibold cursor-pointer">
                  {location.pathname === '/use-proxy' ? <span className="text-green-600 font-bold">&#x2713;</span> : <span className="text-red-400 font-bold">&#x2715;</span>}
                  use proxy to exchange OAuth code for token
                </a>

                <FormSignInViaCreds onSubmit={handleLoginViaCreds} error={null} isLocked={inProcessing} />

                {inProcessing && <PreloaderSpin />}
              </div>
            </>
          )}
        </div>
        {error && (
          <div className="w-full flex flex-col px-8 py-4 min-h-12 rounded-b-2xl bg-red-500 text-left text-md font-semibold relative">
            <p>
              <span className="text-md uppercase text-white">{error.message}</span>
              <span className="bg-black text-white px-2 py-1 text-xs uppercase rounded absolute -top-3 left-6 z-20">{error.provider}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
